/**
 * Feedback and background work.
 *
 * Exports run through Queues to R2 because workbook generation over thousands of rows exceeds the
 * free plan's 10 ms CPU ceiling — that, rather than Queues access, is what makes Workers Paid a
 * Phase 6 requirement (D12).
 */

import { index, integer, sqliteTable, text, uniqueIndex } from "drizzle-orm/sqlite-core";
import { archivable, audited, flag, id, instant, json, ref } from "./columns.js";

export const SURVEY_AUDIENCES = [
  "participants",
  "judges",
  "organizers",
  "volunteers",
  "vendors",
  "public",
] as const;

export const surveys = sqliteTable(
  "surveys",
  {
    id: id(),
    communityId: ref("community_id").notNull(),
    eventId: ref("event_id"),
    name: text("name").notNull(),
    description: text("description"),
    audience: text("audience", { enum: SURVEY_AUDIENCES }).notNull().default("participants"),
    /** Anonymous surveys never record a participant against a response, even when the link knew one. */
    anonymous: flag("anonymous").notNull().default(false),
    formVersionId: ref("form_version_id").notNull(),
    opensAt: instant("opens_at"),
    closesAt: instant("closes_at"),
    ...audited(),
    ...archivable(),
  },
  (table) => [index("surveys_event_idx").on(table.eventId)],
);

export const surveyResponses = sqliteTable(
  "survey_responses",
  {
    id: id(),
    surveyId: ref("survey_id").notNull(),
    communityId: ref("community_id").notNull(),
    formSubmissionId: ref("form_submission_id").notNull(),
    participantId: ref("participant_id"),
    ...audited(),
  },
  (table) => [index("survey_responses_survey_idx").on(table.surveyId)],
);

export const NOTIFICATION_PRIORITIES = ["normal", "important", "urgent"] as const;

/**
 * An item requiring a person's attention — scores missing, a tie to resolve, a payout awaiting
 * approval (D35). Rows are written by the same rules engine that powers readiness, then owned by
 * the recipient: read/unread is theirs, and a notification is never deleted out from under them
 * because the underlying issue resolved — the action link simply reports it resolved.
 */
export const notifications = sqliteTable(
  "notifications",
  {
    id: id(),
    communityId: ref("community_id").notNull(),
    userId: ref("user_id").notNull(),
    eventId: ref("event_id"),
    /** A stable key — `scores_missing`, `tie_unresolved` — so re-evaluation upserts rather than
     *  piling up duplicates of the same fact. */
    type: text("type").notNull(),
    title: text("title").notNull(),
    body: text("body"),
    priority: text("priority", { enum: NOTIFICATION_PRIORITIES }).notNull().default("normal"),
    entityType: text("entity_type"),
    entityId: text("entity_id"),
    /** A management-app path — where "go deal with it" goes. */
    actionPath: text("action_path"),
    readAt: instant("read_at"),
    ...audited(),
  },
  (table) => [
    uniqueIndex("notifications_dedupe_unique").on(
      table.userId,
      table.type,
      table.entityType,
      table.entityId,
    ),
    index("notifications_user_idx").on(table.userId, table.readAt),
  ],
);

export const JOB_STATUSES = ["queued", "running", "complete", "failed"] as const;

export const exportJobs = sqliteTable(
  "export_jobs",
  {
    id: id(),
    communityId: ref("community_id").notNull(),
    requestedBy: ref("requested_by").notNull(),
    type: text("type").notNull(),
    paramsJson: json("params_json"),
    status: text("status", { enum: JOB_STATUSES }).notNull().default("queued"),
    r2Key: text("r2_key"),
    error: text("error"),
    completedAt: instant("completed_at"),
    ...audited(),
  },
  (table) => [index("export_jobs_community_idx").on(table.communityId, table.status)],
);

/**
 * An email that is owed.
 *
 * Delivery goes through Queues, so this table is the record rather than the mechanism — it exists
 * so a failed send is visible to an operator and a resend is a deliberate act. Queues on the free
 * plan retains messages for 24 hours; a consumer failing across a weekend loses them, and this
 * row is what remains.
 */
export const emailJobs = sqliteTable(
  "email_jobs",
  {
    id: id(),
    communityId: ref("community_id").notNull(),
    to: text("to").notNull(),
    template: text("template").notNull(),
    payloadJson: json("payload_json"),
    status: text("status", { enum: JOB_STATUSES }).notNull().default("queued"),
    attempts: integer("attempts").notNull().default(0),
    lastError: text("last_error"),
    sentAt: instant("sent_at"),
    ...audited(),
  },
  (table) => [index("email_jobs_status_idx").on(table.status, table.createdAt)],
);
